import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import render, { removeListener } from '@aytm/lib/onvisible/onVisible'
import {
  createRenderGate,
  RenderGateContext
} from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'
import { ChartFrameContext } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/chart-frame'

// Holds a stand-in in the content's place until the page scrolls it into view, then swaps the
// content in. What the stand-in looks like is the caller's; when to swap is decided here.
const Placeholder = ({ children, height, required = false, loadAll = false, isLoading, onLoad, renderStandIn }) => {
  const [isVisible, setIsVisible] = useState(loadAll)

  const contentRef = useRef(null)
  const standInRef = useRef(null)

  // the export waits on the content, not on the ask: the gate opens once what mounted under it
  // reports itself drawn -- a chart or a cloud still being laid out serializes as an empty frame
  const gateRef = useRef(null)

  useEffect(() => {
    const standIn = standInRef.current
    removeListener(standIn)

    if (!isVisible && !isLoading) {
      render(
        standIn,
        (_element, resolve) => {
          gateRef.current = 'function' == typeof resolve ? createRenderGate(resolve) : null

          setIsVisible(true)
        },
        required
      )
    }

    return () => {
      removeListener(standIn)
    }
  }, [isVisible, isLoading, onLoad])

  // runs after every child's effect, so what registered by now is the whole subtree
  useEffect(() => {
    const gate = gateRef.current
    if (!gate || !isVisible || isLoading) return

    gate.seal()

    return () => gate.cancel()
  }, [isVisible, isLoading])

  // announced from here rather than from the callback above: there the swap has only been asked
  // for, so the content it reports has not mounted yet and the slot it would read is still empty.
  // A host that does not anchor its scroll passes no handler -- a heatmap row is one.
  useEffect(() => {
    if (onLoad && 'function' == typeof onLoad && isVisible) {
      onLoad(contentRef.current, height)
    }
  }, [height, isVisible, onLoad])

  return isVisible && !isLoading ? (
    <ChartFrameContext.Provider value={contentRef}>
      <RenderGateContext.Provider value={gateRef.current}>{children}</RenderGateContext.Provider>
    </ChartFrameContext.Provider>
  ) : (
    renderStandIn(standInRef)
  )
}

Placeholder.displayName = 'Placeholder'

Placeholder.propTypes = {
  children: PropTypes.any,
  height: PropTypes.number,
  required: PropTypes.bool,
  loadAll: PropTypes.bool,
  isLoading: PropTypes.bool,
  onLoad: PropTypes.func,
  renderStandIn: PropTypes.func.isRequired
}

export default Placeholder
